import { CommonModule } from '@angular/common';
import { Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { LogoComponent } from '@scandium-oy/ngx-scandium';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { debounceTime, filter, map, shareReplay } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { NoResultsComponent } from '../components/no-results/no-results.component';
import { Client } from '../models/client.model';
import { Site } from '../models/site.model';
import { User } from '../models/user.model';
import { ClientsService } from '../services/clients.service';
import { NavigationService } from '../services/navigation.service';
import { SitesService } from '../services/sites.service';
import { UsersService } from '../services/users.service';
import { Roles } from '../utility/role';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    NoResultsComponent,
    LogoComponent,
  ],
})
export class SearchComponent {
  private clientsService = inject(ClientsService);
  private navigationService = inject(NavigationService);
  private sitesService = inject(SitesService);
  private usersService = inject(UsersService);

  private searchText$ = new BehaviorSubject('');
  private searched$ = this.searchText$.asObservable().pipe(debounceTime(300));

  inner = input(false);
  searched = output();
  hasText = output<boolean>();

  filteredSites$ = combineLatest([
    this.searched$,
    this.sitesService.getActiveList().pipe(
      filter((sites) => sites.length > 0),
    ),
  ]).pipe(
    map(([text, sites]) => {
      if (text.length < 2) {
        return [];
      }
      return sites.filter((site) =>
        site.name.toLowerCase().includes(text.toLowerCase())
        || site.client?.toLowerCase().includes(text.toLowerCase()),
      );
    }),
    map((sites) => sites.map((site) => {
      site.client$ = site.client ? this.clientsService.get(site.client) : of(null);
      return site;
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  filteredUsers$ = combineLatest([
    this.searched$,
    this.usersService.getList(),
  ]).pipe(
    map(([text, users]) => {
      if (text.length < 2) {
        return [];
      }
      return users.filter((user) =>
        user.displayName.toLowerCase().includes(text.toLowerCase()),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  filteredClients$ = combineLatest([
    this.searched$,
    this.clientsService.getList(),
  ]).pipe(
    map(([text, clients]) => {
      if (text.length < 2) {
        return [];
      }
      return clients.filter((client) =>
        client.name.toLowerCase().includes(text.toLowerCase()) || client.guid.toLowerCase().includes(text.toLowerCase()),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  onChange(event: CustomEvent) {
    const { value } = event.detail;
    this.searchText$.next(value);
    this.hasText.emit(value.length > 0);
  }

  onClient(client: Client) {
    this.navigationService.navigateToClient(client.guid).then(() => {
      this.dismiss();
    });
  }

  onSite(site: Site) {
    this.navigationService.navigateToSite(site.guid).then(() => {
      this.dismiss();
    });
  }

  async onUser(user: User) {
    if ([Roles.worker, Roles.storage].includes(this.usersService.role())) {
      return this.navigationService.navigate(['/profile']).then(() => {
        this.dismiss();
      });
    }
    this.navigationService.navigateToUser(user.guid).then(() => {
      this.dismiss();
    });
  }

  dismiss() {
    this.searchText$.next('');
    this.searched.emit();
  }
}
